"use client";

import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import clsx from "clsx";
import {
  Activity,
  AlertTriangle,
  Ban,
  CheckCircle2,
  Coins,
  Power,
  RefreshCw,
  ShieldAlert,
  TrendingUp,
  Users,
} from "lucide-react";
import { AdminShell } from "@/components/admin-shell";
import { FgCard } from "@/components/fg-card";
import { FgButton } from "@/components/fg-button";
import { FgInput } from "@/components/fg-input";
import { FgToast } from "@/components/fg-toast";
import { EmptyState } from "@/components/empty-state";
import { api } from "@/lib/api";

interface GatewayStatus {
  gatewayEnabled: boolean;
  defaultDailyCapUsd: number;
  services: Record<'stt' | 'tts' | 'llm' | 'exam_full_sim', boolean>;
}

type ServiceKey = 'stt' | 'tts' | 'llm' | 'exam_full_sim';

const SERVICE_META: Record<ServiceKey, { label: string; hint: string }> = {
  stt: { label: 'Speech-to-Text', hint: 'Google Cloud STT v2 — user audio → text.' },
  tts: { label: 'Text-to-Speech', hint: 'Google Cloud TTS — AI reply → MP3.' },
  llm: { label: 'LLM (Gemini)', hint: 'Conversation + grading — bulk of the spend.' },
  exam_full_sim: { label: 'Goethe Full Sim', hint: 'Full 15-20 min AI examiner session.' },
};

interface CostByService {
  service: string;
  total: string;
}

interface TopUser {
  userId: string;
  total: string;
  calls: string;
}

interface DailyCost {
  date: string;
  total: string;
}

export function AiCostPage() {
  const qc = useQueryClient();
  const [userCap, setUserCap] = useState({ userId: "", capUsd: "5" });

  // ── Gateway status ─────────────────────────────────────────────────────
  const status = useQuery<GatewayStatus>({
    queryKey: ["admin", "ai-gateway", "status"],
    queryFn: async () => (await api.get("/api/admin/ai-gateway/status")).data,
    refetchInterval: 10_000,
  });

  const setEnabled = useMutation({
    mutationFn: async (enabled: boolean) => {
      const endpoint = enabled
        ? "/api/admin/ai-gateway/enabled"
        : "/api/admin/ai-gateway/disabled";
      return (await api.post(endpoint)).data;
    },
    onSuccess: (data: { enabled: boolean }) => {
      qc.invalidateQueries({ queryKey: ["admin", "ai-gateway"] });
      FgToast[data.enabled ? "success" : "error"](
        data.enabled ? "AI gateway ENABLED" : "AI gateway DISABLED — all AI calls now return 503",
      );
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Toggle failed"),
  });

  const setService = useMutation({
    mutationFn: async (body: { service: ServiceKey; enabled: boolean }) =>
      (await api.post("/api/admin/ai-gateway/service", body)).data,
    onSuccess: (data: { service: string; enabled: boolean }) => {
      qc.invalidateQueries({ queryKey: ["admin", "ai-gateway"] });
      FgToast[data.enabled ? "success" : "error"](
        `${data.service.toUpperCase()} ${data.enabled ? "enabled" : "disabled"}`,
      );
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Service toggle failed"),
  });

  const setUserCapMutation = useMutation({
    mutationFn: async (body: { userId: string; capUsd: number }) =>
      (await api.post("/api/admin/ai-gateway/user-cap", body)).data,
    onSuccess: (data: { userId: string; capUsd: number }) => {
      FgToast.success(`Cap for ${data.userId.slice(0, 8)}… set to $${data.capUsd}`);
      setUserCap({ userId: "", capUsd: "5" });
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Cap update failed"),
  });

  // ── Cost snapshot ──────────────────────────────────────────────────────
  const byService = useQuery<CostByService[]>({
    queryKey: ["admin", "ai-cost", "by-service", 1],
    queryFn: async () =>
      (await api.get("/api/admin/ai-cost/by-service?days=1")).data,
    refetchInterval: 30_000,
  });

  const topUsers = useQuery<TopUser[]>({
    queryKey: ["admin", "ai-cost", "top-users", 1],
    queryFn: async () =>
      (await api.get("/api/admin/ai-cost/top-users?days=1&limit=10")).data,
    refetchInterval: 30_000,
  });

  const daily = useQuery<DailyCost[]>({
    queryKey: ["admin", "ai-cost", "daily"],
    queryFn: async () =>
      (await api.get("/api/admin/ai-cost/daily?days=30")).data,
    refetchInterval: 60_000,
  });

  const totalToday = useMemo(
    () => (byService.data ?? []).reduce((acc, r) => acc + Number(r.total ?? 0), 0),
    [byService.data],
  );

  return (
    <AdminShell>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[rgba(255,255,255,0.08)] pb-6 mb-8"
      >
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-5 h-5 text-[#FFD60A]" />
            <span className="text-[11px] font-mono font-bold tracking-widest uppercase text-[#FFD60A]">
              AI COST & GUARDRAILS
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#FAFAFA]">
            AI Cost Dashboard
          </h1>
          <p className="mt-1 text-sm text-[#8E8E93]">
            Prometheus metrics · kill-switch · per-user caps · daily SLO
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FgButton
            size="sm"
            variant="secondary"
            onClick={() => {
              qc.invalidateQueries({ queryKey: ["admin", "ai-gateway"] });
              qc.invalidateQueries({ queryKey: ["admin", "ai-cost"] });
            }}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Refresh
          </FgButton>
        </div>
      </motion.div>

      {/* ── Gateway kill-switch banner ──────────────────────────────────── */}
      <FgCard className="p-5 mb-6 border border-[rgba(255,255,255,0.08)]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {status.data?.gatewayEnabled ? (
              <CheckCircle2 className="w-5 h-5 text-[#34C759]" />
            ) : (
              <Ban className="w-5 h-5 text-[#FF453A]" />
            )}
            <div>
              <p className="text-base font-bold text-[#FAFAFA]">
                AI gateway {status.data?.gatewayEnabled ? "enabled" : "DISABLED"}
              </p>
              <p className="text-xs text-[#8E8E93]">
                When disabled, every STT / TTS / LLM call returns 503.
                Use this if a model provider has an outage.
              </p>
            </div>
          </div>
          {status.data?.gatewayEnabled ? (
            <FgButton
              size="sm"
              variant="destructive"
              loading={setEnabled.isPending}
              onClick={() => setEnabled.mutate(false)}
              leftIcon={<Power className="w-4 h-4" />}
            >
              Disable gateway
            </FgButton>
          ) : (
            <FgButton
              size="sm"
              variant="primary"
              loading={setEnabled.isPending}
              onClick={() => setEnabled.mutate(true)}
              leftIcon={<Power className="w-4 h-4" />}
            >
              Re-enable gateway
            </FgButton>
          )}
        </div>
      </FgCard>

      {/* ── Per-service toggles ────────────────────────────────────────── */}
      <FgCard className="p-5 mb-6 border border-[rgba(255,255,255,0.08)]">
        <div className="flex items-center gap-2 mb-3">
          <Power className="w-4 h-4 text-[#FFD60A]" />
          <h2 className="text-sm font-bold text-[#FAFAFA]">Per-service toggles</h2>
        </div>
        <p className="text-xs text-[#8E8E93] mb-4">
          Disable a single AI service without killing the gateway. Useful when
          e.g. Google STT has an outage but the rest of the pipeline is fine.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {(Object.keys(SERVICE_META) as ServiceKey[]).map((svc) => {
            const enabled = status.data?.services?.[svc] ?? true;
            return (
              <FgCard
                key={svc}
                className={clsx(
                  "p-3 border transition-all",
                  enabled
                    ? "border-[rgba(52,199,89,0.4)] bg-[rgba(52,199,89,0.05)]"
                    : "border-[rgba(255,69,58,0.4)] bg-[rgba(255,69,58,0.05)]",
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#8E8E93]">
                    {svc.replace('_', ' ')}
                  </span>
                  {enabled ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#34C759]" />
                  ) : (
                    <Ban className="w-3.5 h-3.5 text-[#FF453A]" />
                  )}
                </div>
                <p className="text-xs font-bold text-[#FAFAFA] mb-0.5">
                  {SERVICE_META[svc].label}
                </p>
                <p className="text-[10px] text-[#8E8E93] mb-2">
                  {SERVICE_META[svc].hint}
                </p>
                <FgButton
                  size="sm"
                  variant={enabled ? "destructive" : "primary"}
                  loading={setService.isPending}
                  onClick={() => setService.mutate({ service: svc, enabled: !enabled })}
                  className="w-full"
                >
                  {enabled ? "Disable" : "Enable"}
                </FgButton>
              </FgCard>
            );
          })}
        </div>
      </FgCard>

      {/* ── Snapshot tiles ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <SnapshotTile
          title="Today's AI spend"
          value={`$${totalToday.toFixed(2)}`}
          hint="UTC day-to-date"
          tone="yellow"
          icon={<Coins size={14} />}
        />
        <SnapshotTile
          title="Default daily cap"
          value={`$${status.data?.defaultDailyCapUsd ?? 5}`}
          hint="per user, fallback"
          tone="blue"
          icon={<ShieldAlert size={14} />}
        />
        <SnapshotTile
          title="Gateway status"
          value={status.data?.gatewayEnabled ? "ON" : "OFF"}
          hint={status.data?.gatewayEnabled ? "Live" : "503 to all calls"}
          tone={status.data?.gatewayEnabled ? "green" : "red"}
          icon={<Power size={14} />}
        />
        <SnapshotTile
          title="Scrape endpoint"
          value="/metrics"
          hint="Prometheus · text"
          tone="neutral"
          icon={<Activity size={14} />}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr,1fr] gap-4">
        {/* ── Per-user cap ─────────────────────────────────────────────── */}
        <FgCard className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-[#FFD60A]" />
            <h2 className="text-sm font-bold text-[#FAFAFA]">Override per-user daily cap</h2>
          </div>
          <p className="text-xs text-[#8E8E93] mb-4">
            Set a per-user USD cap on AI spend. Defaults to the system fallback
            (${status.data?.defaultDailyCapUsd ?? 5}/day). Overrides expire in 1 year.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FgInput
              label="User ID"
              placeholder="uuid"
              value={userCap.userId}
              onChange={(e) =>
                setUserCap((u) => ({ ...u, userId: e.target.value }))
              }
            />
            <FgInput
              label="Daily cap (USD)"
              type="number"
              value={userCap.capUsd}
              onChange={(e) =>
                setUserCap((u) => ({ ...u, capUsd: e.target.value }))
              }
            />
          </div>
          <FgButton
            className="mt-3"
            size="sm"
            variant="primary"
            loading={setUserCapMutation.isPending}
            disabled={!userCap.userId || !userCap.capUsd}
            onClick={() =>
              setUserCapMutation.mutate({
                userId: userCap.userId,
                capUsd: Number(userCap.capUsd),
              })
            }
            leftIcon={<ShieldAlert className="w-4 h-4" />}
          >
            Set per-user cap
          </FgButton>
        </FgCard>

        {/* ── Top users ────────────────────────────────────────────────── */}
        <FgCard className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-[#FFD60A]" />
            <h2 className="text-sm font-bold text-[#FAFAFA]">Top costliest users today</h2>
          </div>
          {(topUsers.data ?? []).length === 0 ? (
            <EmptyState
              tone="muted"
              icon={<TrendingUp className="w-7 h-7" />}
              title="No spend yet today"
              description="AI costs log in real time — once learners start talking to the speaking tutor, this list will populate."
            />
          ) : (
            <div className="space-y-1.5">
              {(topUsers.data ?? []).map((u, idx) => (
                <div
                  key={u.userId}
                  className="flex items-center justify-between rounded-md bg-white/[0.02] border border-[rgba(255,255,255,0.04)] px-2.5 py-1.5 text-xs"
                >
                  <span className="font-mono text-[10px] text-[#8E8E93]">
                    #{idx + 1}
                  </span>
                  <span className="font-mono text-[10px] text-[#FAFAFA] flex-1 truncate ml-2">
                    {u.userId}
                  </span>
                  <span className="font-mono text-[10px] text-[#8E8E93] ml-2">
                    {u.calls} calls
                  </span>
                  <span className="font-mono text-xs font-bold text-[#FFD60A] ml-2">
                    ${Number(u.total).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </FgCard>
      </div>

      {/* ── Daily cost series ────────────────────────────────────────── */}
      <FgCard className="p-5 mt-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-[#FFD60A]" />
          <h2 className="text-sm font-bold text-[#FAFAFA]">Daily AI cost — last 30 days</h2>
        </div>
        <DailyCostChart points={daily.data ?? []} />
      </FgCard>

      {/* ── SLO email + info ─────────────────────────────────────────── */}
      <FgCard className="p-5 mt-4">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-[#FFD60A]" />
          <h2 className="text-sm font-bold text-[#FAFAFA]">Daily SLO email</h2>
        </div>
        <p className="text-xs text-[#8E8E93] mb-3">
          Runs every day at 09:00 UTC. Renders a summary email and sends it to
          <code className="mx-1 rounded bg-white/5 px-1 text-[#FFD60A]">OPS_EMAIL_DISTRIBUTION</code>.
          Configure SMTP in the backend env. Preview the rendered body without sending:
        </p>
        <FgButton
          size="sm"
          variant="secondary"
          onClick={async () => {
            try {
              const html = (await api.get("/api/admin/slo/preview")).data as string;
              const w = window.open("", "_blank");
              if (w) w.document.write(html);
            } catch (e) {
              FgToast.error((e as { message?: string }).message ?? "Preview failed");
            }
          }}
        >
          Open preview
        </FgButton>
      </FgCard>
    </AdminShell>
  );
}

function SnapshotTile({
  title,
  value,
  hint,
  tone,
  icon,
}: {
  title: string;
  value: string;
  hint: string;
  tone: "yellow" | "blue" | "green" | "red" | "neutral";
  icon: React.ReactNode;
}) {
  const colorMap: Record<string, string> = {
    yellow: "text-[#FFD60A]",
    blue: "text-[#0A84FF]",
    green: "text-[#34C759]",
    red: "text-[#FF453A]",
    neutral: "text-[#FAFAFA]",
  };
  return (
    <FgCard className="p-4 flex flex-col justify-between h-28">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#8E8E93]">
          {title}
        </span>
        <div className="w-7 h-7 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-[#FAFAFA]">
          {icon}
        </div>
      </div>
      <div className={clsx("font-mono text-2xl font-black", colorMap[tone])}>
        {value}
      </div>
      <div className="text-[10px] text-[#8E8E93]">{hint}</div>
    </FgCard>
  );
}

function DailyCostChart({ points }: { points: DailyCost[] }) {
  if (points.length === 0) {
    return (
      <div className="text-center py-8">
        <TrendingUp className="w-8 h-8 mx-auto text-[#8E8E93] mb-2" />
        <p className="text-xs text-[#8E8E93]">
          Cost series will appear after the first ai_cost_logs rows are written.
        </p>
      </div>
    );
  }
  const max = Math.max(0.01, ...points.map((p) => Number(p.total)));
  return (
    <div className="flex items-end gap-0.5 h-32">
      {points.map((p) => {
        const h = Math.max(2, (Number(p.total) / max) * 100);
        return (
          <div
            key={p.date}
            title={`${p.date} · $${Number(p.total).toFixed(2)}`}
            className="flex-1 rounded-t bg-[#FFD60A]/80 hover:bg-[#FFD60A] transition-colors"
            style={{ height: `${h}%` }}
          />
        );
      })}
    </div>
  );
}
